import type { HourlyEntry, PlayableWindowResult } from '../types';

// Course-specific config. Hardcoded for now — easy to move to env vars or a
// user-preferences D1 table once we have multi-user auth.
export const HOME_COURSE = {
  name: 'Pauma Valley Country Club',
  walkingOnlyRainIn: 0.75,
};

// Lightning detection keywords as they appear in NWS `shortForecast` strings.
// NWS is verbose and inconsistent — cover the main surface forms.
const LIGHTNING_KEYWORDS = ['thunder', 'lightning', 't-storm', 'tstm'];

export function hourHasLightning(condition: string | null | undefined): boolean {
  const c = (condition ?? '').toLowerCase();
  return LIGHTNING_KEYWORDS.some((k) => c.includes(k));
}

// Per user preference: only exclude hours with forecast lightning. Wind, rain
// probability, temp, UV — all considered playable. Returns contiguous runs of
// non-lightning hours within the horizon.
export function findPlayableWindows(
  hourly: HourlyEntry[],
  horizonHours = 24,
): PlayableWindowResult[] {
  const out: PlayableWindowResult[] = [];
  const n = Math.min(horizonHours, hourly.length);

  let runStart = -1;
  let sumTemp = 0;
  let maxWind = 0;
  let maxGust = 0;
  let maxUv = 0;
  let maxPrecip = 0;

  const flush = (endIdx: number) => {
    if (runStart < 0) return;
    const length = endIdx - runStart;
    if (length >= 1) {
      out.push({
        start: hourly[runStart].time,
        end: hourly[endIdx - 1].time,
        hours: length,
        avgTempF: Math.round(sumTemp / length),
        maxWindMph: Math.round(maxWind),
        maxGustMph: Math.round(maxGust),
        maxUv: Math.round(maxUv * 10) / 10,
        maxPrecipPct: maxPrecip,
      });
    }
    runStart = -1;
    sumTemp = 0;
    maxWind = 0;
    maxGust = 0;
    maxUv = 0;
    maxPrecip = 0;
  };

  for (let i = 0; i < n; i++) {
    const h = hourly[i];
    if (hourHasLightning(h.condition)) {
      flush(i);
      continue;
    }
    if (runStart < 0) runStart = i;
    sumTemp += h.tempF;
    if (h.windMph != null) maxWind = Math.max(maxWind, h.windMph);
    if (h.gustMph != null) maxGust = Math.max(maxGust, h.gustMph);
    if (h.uvIndex != null) maxUv = Math.max(maxUv, h.uvIndex);
    maxPrecip = Math.max(maxPrecip, h.precipPct ?? 0);
  }
  flush(n);
  return out;
}

export function courseRainWarning(rain48hIn: number): string | null {
  if (rain48hIn < HOME_COURSE.walkingOnlyRainIn) return null;
  return `${rain48hIn.toFixed(2)}" of rain in the last 48h — ${HOME_COURSE.name} likely walking-only today.`;
}
